import { stripTypename } from "@apollo/client/utilities";
import {
  Button,
  Callout,
  Card,
  ControlGroup,
  FormGroup,
  InputGroup,
  MenuItem,
  NumericInput,
  ProgressBar,
} from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import {
  getComponentHelperText,
  getComponentIntent,
  SCREEN_TYPE_TO_DISPLAY,
  type SaveToolbarProps,
} from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreateProfileDocument,
  DeleteProfileDocument,
  GetProfileDocument,
  GetProfileGridDocument,
  GetProfileListNameIdDocument,
  OrientationType,
  Resolution,
  ScreenType,
  UpdateProfileDocument,
  useGetProfileQuery,
  type ProfileModel,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { type ValidationError } from "class-validator";
import { produce, type WritableDraft } from "immer";
import { useCallback, useEffect, useState } from "react";
import {
  EDIT_CARD_WIDE_WIDTH_CLASS,
  EDIT_CARD_WIDTH_CLASS,
  ORIENTATION_TYPE_TO_DISPLAY,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import { WallPreviewProfile } from "../../../WallPreview/WallPreviewProfile";
import { ProfileBackgroundMediaListEditor } from "./ProfileBackgroundMediaListEditor";
import { ProfileLayout } from "./ProfileLayoutsEditor";
import { ProfileRestingMediaListEditor } from "./ProfileRestingMediaListEditor";

const defaultProfile: ProfileModel = {
  name: "",
  organization: "",
  resting: {
    staleStateTimeout: 60,
  },
  screen: {
    count: 1,
    columns: 1,
    orientation: OrientationType.Portrait,
    resolution: Resolution.Fhd,
    rows: 1,
    size: {
      width: 0,
      height: 0,
    },
  },
  type: ScreenType.Tv,
};

type ProfileEditProps = {
  id?: string;
  toolbarProps?: Partial<SaveToolbarProps>;
};

export function ProfileEdit({ id, toolbarProps }: ProfileEditProps) {
  const [profileState, setProfileState] =
    useState<ProfileModel>(defaultProfile);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const updateProfile = useCallback(
    (callback: (draft: WritableDraft<ProfileModel>) => void) => {
      setProfileState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataProfile,
    loading: loadingProfile,
    error: errorProfile,
  } = useGetProfileQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectOnProfileDataChanged() {
      if (dataProfile?.item) {
        setProfileState(dataProfile.item);
      }
    },
    [dataProfile?.item],
  );

  useUpdateSelectedOrganization(dataProfile?.item?.organization);

  if (loadingProfile) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorProfile) {
    return (
      <Callout intent="danger" title="Failed Loading Canvas">
        <p>Failed loading canvas item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingProfile}
        title="Canvas"
        name={dataProfile?.item?.name}
      />
      {!!id && (
        <div className={"flex-0 mt-4"}>
          <WallPreviewProfile profile={id} version={profileState._version} />
        </div>
      )}
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
            intent={getComponentIntent<ProfileModel>("name", errors)}
            helperText={getComponentHelperText<ProfileModel>("name", errors)}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={profileState.name ?? ""}
              onValueChange={(value) =>
                updateProfile((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            fill
            label="Display"
            intent={getComponentIntent("type", errors)}
            helperText={getComponentHelperText("type", errors)}
          >
            <Select<ScreenType>
              activeItem={profileState.type}
              disabled={disabled}
              fill
              filterable={false}
              items={Object.values(ScreenType)}
              itemRenderer={(type, { handleClick, handleFocus, modifiers }) => (
                <MenuItem
                  active={modifiers.active}
                  disabled={modifiers.disabled}
                  key={type}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  roleStructure="listoption"
                  text={SCREEN_TYPE_TO_DISPLAY[type]}
                />
              )}
              popoverProps={{ minimal: true }}
              onItemSelect={(type) =>
                updateProfile((draft) => {
                  draft.type = type;
                })
              }
            >
              <Button
                alignText="left"
                disabled={disabled}
                fill
                minimal
                outlined
                rightIcon="caret-down"
                text={
                  profileState.type
                    ? SCREEN_TYPE_TO_DISPLAY[profileState.type]
                    : "Select"
                }
              />
            </Select>
          </FormGroup>
          {profileState.type === ScreenType.Tv && (
            <>
              <ControlGroup fill>
                <FormGroup
                  disabled={disabled}
                  fill
                  label="Orientation"
                  intent={getComponentIntent("orientation", errors)}
                  helperText={getComponentHelperText("orientation", errors)}
                >
                  <Select<OrientationType>
                    activeItem={profileState.screen?.orientation}
                    disabled={disabled}
                    fill
                    filterable={false}
                    items={Object.values(OrientationType)}
                    itemRenderer={(
                      type,
                      { handleClick, handleFocus, modifiers },
                    ) => (
                      <MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={type}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        text={ORIENTATION_TYPE_TO_DISPLAY[type]}
                      />
                    )}
                    popoverProps={{ minimal: true }}
                    onItemSelect={(type) =>
                      updateProfile((draft) => {
                        draft.screen = {
                          ...draft.screen,
                          orientation: type,
                        };
                      })
                    }
                  >
                    <Button
                      alignText="left"
                      disabled={disabled}
                      fill
                      minimal
                      outlined
                      rightIcon="caret-down"
                      text={
                        profileState.screen?.orientation
                          ? ORIENTATION_TYPE_TO_DISPLAY[
                              profileState.screen?.orientation
                            ]
                          : "Select"
                      }
                    />
                  </Select>
                </FormGroup>
                <FormGroup
                  className="ml-4"
                  disabled={disabled}
                  fill
                  label="Resolution"
                  intent={getComponentIntent("resolution", errors)}
                  helperText={getComponentHelperText("resolution", errors)}
                >
                  <Select<Resolution>
                    activeItem={profileState.screen?.resolution}
                    disabled={disabled}
                    fill
                    filterable={false}
                    items={Object.values(Resolution)}
                    itemRenderer={(
                      resolution,
                      { handleClick, handleFocus, modifiers },
                    ) => (
                      <MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={resolution}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        text={resolution}
                      />
                    )}
                    popoverProps={{ minimal: true }}
                    onItemSelect={(resolution) =>
                      updateProfile((draft) => {
                        draft.screen = {
                          ...draft.screen,
                          resolution: resolution,
                        };
                      })
                    }
                  >
                    <Button
                      alignText="left"
                      disabled={disabled}
                      fill
                      minimal
                      outlined
                      rightIcon="caret-down"
                      text={profileState.screen?.resolution ?? "Select"}
                    />
                  </Select>
                </FormGroup>
              </ControlGroup>
              <ControlGroup fill>
                <FormGroup
                  disabled={disabled}
                  fill
                  label="Screen Count"
                  intent={getComponentIntent("count", errors)}
                  helperText={getComponentHelperText("count", errors)}
                >
                  <NumericInput
                    min={1}
                    allowNumericCharactersOnly
                    fill
                    value={profileState.screen?.count ?? 1}
                    onValueChange={(value) => {
                      updateProfile((draft) => {
                        draft.screen = {
                          ...draft.screen,
                          count: value,
                        };
                      });
                    }}
                  />
                </FormGroup>
                <FormGroup
                  className="ml-4"
                  disabled={disabled}
                  fill
                  label="Rows"
                  intent={getComponentIntent("rows", errors)}
                  helperText={getComponentHelperText("rows", errors)}
                >
                  <NumericInput
                    min={1}
                    allowNumericCharactersOnly
                    fill
                    value={profileState.screen?.rows ?? 1}
                    onValueChange={(value) => {
                      updateProfile((draft) => {
                        draft.screen = {
                          ...draft.screen,
                          rows: value,
                        };
                      });
                    }}
                  />
                </FormGroup>
                <FormGroup
                  className="ml-4"
                  disabled={disabled}
                  fill
                  label="Columns"
                  intent={getComponentIntent("columns", errors)}
                  helperText={getComponentHelperText("columns", errors)}
                >
                  <NumericInput
                    min={1}
                    allowNumericCharactersOnly
                    fill
                    value={profileState.screen?.columns ?? 1}
                    onValueChange={(value) => {
                      updateProfile((draft) => {
                        draft.screen = {
                          ...draft.screen,
                          columns: value,
                        };
                      });
                    }}
                  />
                </FormGroup>
              </ControlGroup>
            </>
          )}
          {profileState.type === ScreenType.Custom && (
            <ControlGroup fill>
              <FormGroup
                disabled={disabled}
                label="LED Width"
                intent={getComponentIntent("width", errors)}
                helperText={getComponentHelperText("width", errors)}
              >
                <NumericInput
                  min={0}
                  allowNumericCharactersOnly
                  fill
                  value={profileState.screen?.size?.width ?? 0}
                  onValueChange={(value) => {
                    updateProfile((draft) => {
                      draft.screen = {
                        ...draft.screen,
                        size: {
                          ...draft.screen?.size,
                          width: value,
                        },
                      };
                    });
                  }}
                />
              </FormGroup>
              <FormGroup
                className="ml-4"
                disabled={disabled}
                label="LED Height"
                intent={getComponentIntent("height", errors)}
                helperText={getComponentHelperText("height", errors)}
              >
                <NumericInput
                  min={0}
                  allowNumericCharactersOnly
                  fill
                  value={profileState.screen?.size?.height ?? 0}
                  onValueChange={(value) => {
                    updateProfile((draft) => {
                      draft.screen = {
                        ...draft.screen,
                        size: {
                          ...draft.screen?.size,
                          height: value,
                        },
                      };
                    });
                  }}
                />
              </FormGroup>
            </ControlGroup>
          )}
        </div>
        <hr className="mb-8 mt-4" />
        <div className={EDIT_CARD_WIDE_WIDTH_CLASS}>
          <ProfileLayout
            disabled={disabled}
            profile={profileState}
            onProfileChanged={updateProfile}
          />
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <ProfileRestingMediaListEditor
            disabled={disabled}
            profile={profileState}
            onProfileChanged={updateProfile}
          />
        </div>
      </Card>
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <ProfileBackgroundMediaListEditor
            disabled={disabled}
            profile={profileState}
            onProfileChanged={updateProfile}
          />
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateProfileDocument}
            documentDelete={DeleteProfileDocument}
            documentUpdate={UpdateProfileDocument}
            path={SidebarEntryPath.profiles}
            isNew={!id}
            refetchQueries={[
              GetProfileDocument,
              GetProfileGridDocument,
              GetProfileListNameIdDocument,
            ]}
            title="Canvas"
            validationModel="profile-update"
            variablesCreate={stripTypename(profileState)}
            variablesUpdate={stripTypename(profileState)}
            variablesDelete={{ id }}
            {...toolbarProps}
            onInflight={(inflight) => {
              setDisabled(inflight);

              if (inflight) {
                setErrors([]);
              }
            }}
            onValidationFailed={(errors) => {
              setErrors(errors);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
